import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import Icon from '../AppIcon';

/**
 * AchievementList Component
 * Displays all achievements for a game with unlock status and progress
 *
 * Usage:
 * <AchievementList gameId="game_uuid" />
 */
export default function AchievementList({ gameId, showFilter = true }) {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unlocked, locked
  const [sortBy, setSortBy] = useState('name'); // name, rarity, xp, unlock_date

  useEffect(() => {
    if (gameId) {
      loadAchievements();
    }
  }, [gameId, user?.id]);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all achievements for the game
      const { data: achievementsData, error: achievementsError } = await supabase
        .from('achievements')
        .select('*')
        .eq('game_id', gameId)
        .order('created_at', { ascending: true });

      if (achievementsError) throw achievementsError;

      // If user is logged in, get their unlock status
      if (user?.id && achievementsData && achievementsData.length > 0) {
        const achievementIds = achievementsData.map(a => a.id);

        const { data: unlocksData, error: unlocksError } = await supabase
          .from('user_achievements')
          .select('achievement_id, unlocked_at, progress')
          .eq('user_id', user.id)
          .in('achievement_id', achievementIds);

        if (unlocksError) throw unlocksError;

        // Create map of unlocked achievements
        const unlocksMap = {};
        (unlocksData || []).forEach(unlock => {
          unlocksMap[unlock.achievement_id] = unlock;
        });

        // Merge unlock status with achievements
        const enriched = achievementsData.map(achievement => ({
          ...achievement,
          unlocked: !!unlocksMap[achievement.id],
          unlockedAt: unlocksMap[achievement.id]?.unlocked_at,
          progress: unlocksMap[achievement.id]?.progress || 0
        }));

        setAchievements(enriched);
      } else {
        setAchievements(achievementsData || []);
      }

    } catch (err) {
      console.error('Error loading achievements:', err);
      setError('Failed to load achievements');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAchievements = () => {
    let filtered = achievements;

    // Apply filter
    if (filter === 'unlocked') {
      filtered = filtered.filter(a => a.unlocked);
    } else if (filter === 'locked') {
      filtered = filtered.filter(a => !a.unlocked);
    }

    // Apply sort
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'rarity':
          const rarityOrder = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4 };
          return (rarityOrder[b.rarity] || 0) - (rarityOrder[a.rarity] || 0);
        case 'xp':
          return (b.xp_reward || 0) - (a.xp_reward || 0);
        case 'unlock_date':
          if (!a.unlockedAt) return 1;
          if (!b.unlockedAt) return -1;
          return new Date(b.unlockedAt) - new Date(a.unlockedAt);
        case 'name':
        default:
          return (a.name || '').localeCompare(b.name || '');
      }
    });

    return filtered;
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common':
        return 'border-slate-400 bg-slate-50 dark:bg-slate-900';
      case 'uncommon':
        return 'border-green-400 bg-green-50 dark:bg-green-900/20';
      case 'rare':
        return 'border-blue-400 bg-blue-50 dark:bg-blue-900/20';
      case 'epic':
        return 'border-purple-400 bg-purple-50 dark:bg-purple-900/20';
      case 'legendary':
        return 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      default:
        return 'border-border bg-card';
    }
  };

  const getRarityBadgeColor = (rarity) => {
    switch (rarity) {
      case 'common':
        return 'bg-slate-500';
      case 'uncommon':
        return 'bg-green-500';
      case 'rare':
        return 'bg-blue-500';
      case 'epic':
        return 'bg-purple-500';
      case 'legendary':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const totalCount = achievements.length;
  const completionPercentage = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

  const filteredAchievements = getFilteredAchievements();

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading achievements...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress Summary */}
      {user && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-semibold text-foreground">Achievement Progress</h4>
              <p className="text-sm text-muted-foreground">{unlockedCount} of {totalCount} unlocked</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-primary">{completionPercentage}%</div>
              <p className="text-xs text-muted-foreground">Complete</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-muted rounded-full h-3">
            <div
              className="bg-primary h-3 rounded-full transition-all duration-500"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Filters and sorting */}
      {showFilter && (
        <div className="bg-card border border-border rounded-lg p-4 flex flex-wrap items-center gap-3">
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Icon name="Filter" size={16} className="text-muted-foreground" />
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              {['all', 'unlocked', 'locked'].map(filterOption => (
                <button
                  key={filterOption}
                  onClick={() => setFilter(filterOption)}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors capitalize ${
                    filter === filterOption
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {filterOption}
                </button>
              ))}
            </div>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <Icon name="ArrowUpDown" size={16} className="text-muted-foreground" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1 bg-muted border border-border rounded text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="name">Name</option>
              <option value="rarity">Rarity</option>
              <option value="xp">XP</option>
              {user && <option value="unlock_date">Unlock Date</option>}
            </select>
          </div>

          {/* Count */}
          <div className="ml-auto text-sm text-muted-foreground">
            {filteredAchievements.length} achievement{filteredAchievements.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {/* Achievements grid */}
      {filteredAchievements.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-8 text-center">
          <Icon name="Award" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Achievements</h3>
          <p className="text-sm text-muted-foreground">
            {filter === 'unlocked' && 'You haven\'t unlocked any achievements yet.'}
            {filter === 'locked' && 'All achievements unlocked! Great job!'}
            {filter === 'all' && 'No achievements available for this game.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAchievements.map(achievement => (
            <div
              key={achievement.id}
              className={`border-2 rounded-lg p-4 transition-all ${getRarityColor(achievement.rarity)} ${
                achievement.unlocked ? '' : 'opacity-60'
              }`}
            >
              {/* Header */}
              <div className="flex items-start gap-3 mb-3">
                {/* Icon */}
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  achievement.unlocked ? 'bg-primary/20' : 'bg-muted'
                }`}>
                  {achievement.icon_url ? (
                    <img
                      src={achievement.icon_url}
                      alt={achievement.name}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <Icon
                      name={achievement.unlocked ? 'Award' : 'Lock'}
                      size={24}
                      className={achievement.unlocked ? 'text-primary' : 'text-muted-foreground'}
                    />
                  )}
                </div>

                {/* Name and rarity */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-foreground mb-1 line-clamp-1">
                    {achievement.name}
                  </h4>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold text-white uppercase ${getRarityBadgeColor(achievement.rarity)}`}>
                      {achievement.rarity || 'Common'}
                    </span>
                    {achievement.xp_reward && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Icon name="Zap" size={12} />
                        {achievement.xp_reward} XP
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {achievement.description && (
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {achievement.description}
                </p>
              )}

              {/* Progress or unlock date */}
              {achievement.unlocked ? (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Icon name="Check" size={14} className="text-green-500" />
                  <span>Unlocked {formatDate(achievement.unlockedAt)}</span>
                </div>
              ) : achievement.progress > 0 ? (
                <div>
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Progress</span>
                    <span>{achievement.progress}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${achievement.progress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Icon name="Lock" size={14} />
                  <span>Locked</span>
                </div>
              )}

              {/* Unlock rate (if available) */}
              {achievement.unlock_rate && (
                <div className="mt-2 text-xs text-muted-foreground">
                  {(achievement.unlock_rate * 100).toFixed(1)}% of players unlocked
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
